import React from "react";
import { motion } from "framer-motion";

export default function Maintenance() {
  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#070B16] text-white">
      {/* Background blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-purple-500/25 blur-[110px]" />
        <div className="absolute -bottom-44 -right-44 h-[560px] w-[560px] rounded-full bg-cyan-400/20 blur-[120px]" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-blue-500/10 blur-[130px]" />
      </div>

      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none bg-[linear-gradient(to_right,rgba(255,255,255,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.14)_1px,transparent_1px)] bg-[size:44px_44px]" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-5 py-16">
        <motion.div
          initial={{ opacity: 0, y: 14, scale: 0.99 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="w-full max-w-3xl"
        >
          <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl shadow-black/40 p-6 sm:p-10">
            {/* Top badge */}
            <div className="flex items-center justify-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-40"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                </span>
                Platform upgrade in progress
              </div>
            </div>

            {/* Icon */}
            <div className="mt-6 flex justify-center">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-purple-500/40 to-cyan-400/25 border border-white/10 flex items-center justify-center shadow-lg shadow-black/30">
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="opacity-95"
                >
                  <path
                    d="M12 6V3M12 21v-3M18.364 5.636l-2.121 2.121M7.757 16.243l-2.121 2.121M21 12h-3M6 12H3M18.364 18.364l-2.121-2.121M7.757 7.757 5.636 5.636"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                  <path
                    d="M15.5 12a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                  />
                </svg>
              </div>
            </div>

            {/* Title */}
            <div className="mt-6 text-center">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-cyan-200">
                We’re building something great
            </span>
            </h1>
              <p className="mt-3 text-base sm:text-lg text-white/75 leading-relaxed">
                Coinzetto is temporarily in maintenance mode while we strengthen platform security,
                improve reliability, and align with regulatory requirements.
              </p>
            </div>

            {/* Highlights */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm font-semibold">Security upgrades</div>
                <div className="mt-1 text-sm text-white/70">
                  Stronger protections for accounts, funds, and withdrawals.
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm font-semibold">Compliance readiness</div>
                <div className="mt-1 text-sm text-white/70">
                  Enhancements to meet regulatory expectations and reporting.
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm font-semibold">Better experience</div>
                <div className="mt-1 text-sm text-white/70">
                  Faster UI, smoother trade flow, and improved support tools.
                </div>
              </div>
            </div>

            {/* CTA + status */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-white/70 text-center sm:text-left">
                Need help? Email{" "}
                <span className="text-white font-semibold">support@coinzetto.com</span>
                <div className="mt-1 text-xs text-white/50">
                  We’ll respond as soon as possible.
                </div>
              </div>

              <motion.a
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href="/"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white text-[#070B16] px-5 py-3 text-sm font-semibold shadow-lg shadow-black/30 hover:bg-white/95 transition"
              >
                Refresh
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 12a9 9 0 1 1-2.64-6.36"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M21 3v6h-6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.a>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-white/10 text-center text-xs text-white/50">
              © {year} Coinzetto • Thank you for your patience — we’ll be back soon.
            </div>
          </div>

        </motion.div>
      </div>
    </div>
  );
}
